import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Plus, Package, Trash2, Download, Mail, Printer, CheckCircle2, Loader2, ClipboardList, Search,
  RotateCcw, X, Truck, PoundSterling, ChevronLeft, ChevronRight, ChevronDown, Calendar, ArrowLeft,
  Send, Eraser,
} from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { useV3Window } from '../v3/useV3Window';

const API = process.env.REACT_APP_BACKEND_URL;
const B29_URL = `${API}/api/b29-transfers`;
const ORANGE = '#ee7203';
const COLLECTION_ADDRESS = 'COLLECTION';
const STOCK_IN_LABEL = 'IN TO OUR STOCK';

// Layout matches the Customer Management modal exactly: [tab rail][main modal][slide-out panel]. With the panel open the
// modal and the panel become two EQUAL halves of the space to the right of the rail; closed, the modal is capped at
// MODAL_MAX_W (the same width as Customer Management).
const MODAL_MAX_W = 1000;
const RAIL_W = 40;
const SHELL_GAP = 12;
const HALF_WIDTH = `calc((100% - ${RAIL_W + SHELL_GAP}px) / 2)`;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fieldCardClass = 'rounded-xl border border-gray-200 bg-white shadow-sm p-3';
const inputClass = 'rounded-lg border-2 border-gray-300 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 h-10';
const headerBtnClass = 'inline-flex items-center gap-1.5 rounded-xl h-9 px-3 border-2 bg-white text-xs font-bold whitespace-nowrap transition-all shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed';
const actionBtnClass = 'inline-flex items-center justify-center gap-1.5 rounded-xl h-10 px-3 border-2 text-xs font-bold whitespace-nowrap transition-all shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed';

// ── Small helpers ───────────────────────────────────────────────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayIso = () => isoDate(new Date());

/** YYYY-MM-DD (or a full ISO timestamp) -> dd/mm/yy */
const fmtDMY = (iso) => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : String(iso);
};

const money = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const makeMonthRange = (year, month) => ({
  kind: 'month',
  year,
  month,
  from: `${year}-${pad2(month + 1)}-01`,
  to: `${year}-${pad2(month + 1)}-${pad2(new Date(year, month + 1, 0).getDate())}`,
  label: `${MONTH_NAMES[month]} ${year}`,
});

const makeCustomRange = (from, to) => ({
  kind: 'range',
  from,
  to,
  label: `${fmtDMY(from)} – ${fmtDMY(to)}`,
});

const currentMonthRange = () => {
  const now = new Date();
  return makeMonthRange(now.getFullYear(), now.getMonth());
};

const emptyLine = () => ({ id: Date.now() + Math.random(), product_code: '', description: '', quantity: 1, price: 0, supplier: '', tier_label: '', is_carriage: false });

const toApiItem = (i) => ({
  product_code: i.product_code || '',
  description: i.description || '',
  quantity: parseFloat(i.quantity) || 0,
  price: parseFloat(i.price) || 0,
  supplier: i.supplier || '',
  tier_label: i.tier_label || '',
  is_carriage: !!i.is_carriage,
});

/** Unique supplier names on a transfer's real (non-carriage) lines. */
const transferSuppliers = (transfer) => {
  const names = [];
  (transfer.items || []).forEach((i) => {
    const name = (i.supplier || '').trim();
    if (name && !i.is_carriage && !names.includes(name)) names.push(name);
  });
  return names;
};

/** Settings > Pricing > B29's tiered cost matrix auto-fill, on product code / qty blur. */
const useB29CostLookup = () => {
  const lookup = async (code, qty) => {
    if (!code) return null;
    try {
      const res = await api.get(`${B29_URL}/lookup-cost/${encodeURIComponent(code.trim().toUpperCase())}`, { params: { qty: qty || 1 } });
      return res.data?.found ? res.data : null;
    } catch {
      return null;
    }
  };
  return lookup;
};

// ── Actions shared by the transfer cards and the New / Edit Transfer page ───────────────────────────────────────────

const downloadTransferExcel = async (transfer) => {
  try {
    const res = await api.get(`${B29_URL}/${transfer.id}/excel`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `B29_Stock_Transfer_${transfer.order_no || transfer.transfer_ref}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  } catch {
    toast.error('Failed to download Excel');
  }
};

const printTransfer = (transfer) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) { toast.error('Please allow popups to print'); return; }
  const lines = transfer.items || [];
  const total = lines.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.price) || 0), 0);
  const cell = 'padding:6px;border:1px solid #ddd;';
  const rows = lines.map((i) => `<tr>
      <td style="${cell}">${escapeHtml(i.product_code)}</td>
      <td style="${cell}">${escapeHtml(i.description)}</td>
      <td style="${cell}">${escapeHtml(i.supplier)}</td>
      <td style="${cell}text-align:center;">${parseFloat(i.quantity) || 0}</td>
      <td style="${cell}text-align:right;">${money(i.price)}</td>
    </tr>`).join('');
  printWindow.document.write(`
    <html><head><title>B29 Stock Transfer ${escapeHtml(transfer.transfer_ref)}</title></head>
    <body style="font-family: Arial, sans-serif; padding: 20px;">
      <h2 style="color:${ORANGE}">B29 Stock Transfer Form</h2>
      <p><b>Date:</b> ${escapeHtml(fmtDMY(transfer.date))} &nbsp; <b>Branch:</b> ${escapeHtml(transfer.branch)} &nbsp; <b>Order No:</b> ${escapeHtml(transfer.order_no)}</p>
      <p><b>Customer:</b> ${escapeHtml(transfer.customer_name)} &nbsp; <b>Requested delivery:</b> ${escapeHtml(fmtDMY(transfer.req_date))}</p>
      <p><b>Requested by:</b> ${escapeHtml(transfer.person)}</p>
      <p style="background:#fef3c7;border:2px solid #f59e0b;border-radius:8px;padding:10px;"><b>Delivery Address:</b> ${escapeHtml(transfer.delivery_address) || '<span style="color:#b91c1c;font-weight:bold;">NOT SET — check before dispatch</span>'}</p>
      <table style="width:100%;border-collapse:collapse;margin-top:10px;">
        <thead><tr style="background:${ORANGE};color:white;">
          <th style="padding:6px;">Code</th><th style="padding:6px;">Description</th><th style="padding:6px;">Supplier</th><th style="padding:6px;">Qty</th><th style="padding:6px;">Price</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="text-align:right;margin-top:10px;"><b>Total (incl. carriage):</b> ${money(total)}</p>
      <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
    </body></html>
  `);
  printWindow.document.close();
};

const buildDefaultEmailText = (transfer, user) => {
  const name = user?.full_name || user?.username || user?.email || '';
  const lines = [
    'Hi,',
    '',
    'Please find attached the B29 Stock Transfer request.',
    '',
    `Branch: ${transfer.branch || ''}`,
    `Order No: ${transfer.order_no || ''}`,
  ];
  if (transfer.customer_name) lines.push(`Customer: ${transfer.customer_name}`);
  lines.push(`Requested delivery: ${fmtDMY(transfer.req_date) || 'Not set'}`);
  lines.push(`Delivery address: ${transfer.delivery_address || 'NOT SET — please check before dispatch'}`);
  lines.push('', 'Kind regards,', name);
  return lines.join('\n');
};

// ── Email compose modal (a real, editable email — default text with Clear / Reset buttons) ──────────────────────────

const EmailComposeModal = ({ transfer, user, onClose, onSent }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const defaultText = buildDefaultEmailText(transfer, user);
  const v3w = useV3Window(true, 640, { single: true });
  const [text, setText] = useState(defaultText);
  const [sending, setSending] = useState(false);
  const userAddress = user?.username || user?.email || '';

  const send = async () => {
    if (!text.trim()) { toast.error('Write a message first'); return; }
    setSending(true);
    try {
      const res = await api.post(`${B29_URL}/${transfer.id}/email`, { message: text });
      toast.success(res.data?.message || 'Emailed');
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to email transfer');
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center p-4 bg-black/50 ${v3w.className}`}
      {...v3w.attrs}
      style={{ top: modalUiV2 ? activityBar.headerHeight : 0, zIndex: 120, ...v3w.style }}
      onClick={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}
      data-testid="b29-email-overlay"
    >
      <div className="w-full max-w-[620px] rounded-3xl bg-white shadow-2xl border-2 border-orange-300" data-testid="b29-email-modal">
        <div className="v3-modal-bar flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100">
          <div className="min-w-0">
            <h3 className="text-base font-extrabold text-gray-900 leading-tight">Email B29 Transfer</h3>
            <p className="text-[11px] text-gray-500 truncate">
              {transfer.branch} · {transfer.order_no ? `Order ${transfer.order_no}` : transfer.transfer_ref}
            </p>
          </div>
          <button onClick={onClose} disabled={sending} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-600">
            Sent to the B29 email address set in Settings &gt; Pricing &gt; B29, and copied to you ({userAddress || 'your login email'}). The transfer is attached as the B29 Excel form. Replies come back to you.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            placeholder="Write your email here…"
            className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none resize-none"
            data-testid="b29-email-text"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setText('')}
                className={`${actionBtnClass} border-gray-400 text-gray-700 bg-white h-9`}
                data-testid="b29-email-clear"
              >
                <Eraser className="w-3.5 h-3.5" /> Clear
              </button>
              <button
                type="button"
                onClick={() => setText(defaultText)}
                className={`${actionBtnClass} border-orange-400 text-orange-700 bg-white h-9`}
                data-testid="b29-email-reset"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reset to default text
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} disabled={sending} className={`${actionBtnClass} border-gray-300 text-gray-700 bg-white h-9`}>
                Cancel
              </button>
              <button
                type="button"
                onClick={send}
                disabled={sending || !text.trim()}
                className={`${actionBtnClass} border-orange-500 text-white h-9`}
                style={{ backgroundColor: ORANGE }}
                data-testid="b29-email-send"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Email
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ── Month pill: shows which period the list covers; click to pick another month or a date range ─────────────────────

const MonthPill = ({ range, onChange }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('month');
  const [pickerYear, setPickerYear] = useState(range.kind === 'month' ? range.year : new Date().getFullYear());
  const [fromVal, setFromVal] = useState(range.from);
  const [toVal, setToVal] = useState(range.to);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = () => {
    if (!open) {
      setFromVal(range.from);
      setToVal(range.to);
      setPickerYear(range.kind === 'month' ? range.year : new Date().getFullYear());
      setTab(range.kind === 'range' ? 'range' : 'month');
    }
    setOpen((v) => !v);
  };

  const pickMonth = (month) => {
    onChange(makeMonthRange(pickerYear, month));
    setOpen(false);
  };

  const applyRange = () => {
    if (!fromVal || !toVal) { toast.error('Pick both dates'); return; }
    if (fromVal > toVal) { toast.error('The start date must be before the end date'); return; }
    onChange(makeCustomRange(fromVal, toVal));
    setOpen(false);
  };

  const now = new Date();

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-2 rounded-full h-9 px-4 text-sm font-extrabold text-white shadow-md hover:shadow-lg active:scale-95 transition-all"
        style={{ backgroundColor: ORANGE }}
        title="Change month or pick a date range"
        data-testid="b29-month-pill"
      >
        <Calendar className="w-4 h-4" />
        {range.label}
        <ChevronDown className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-50 w-72 bg-white rounded-xl shadow-xl p-4 border border-gray-200" data-testid="b29-month-popover">
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs font-semibold mb-3">
            <button
              type="button"
              onClick={() => setTab('month')}
              className={`flex-1 py-1.5 transition-colors ${tab === 'month' ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              style={tab === 'month' ? { backgroundColor: ORANGE } : {}}
              data-testid="b29-month-tab"
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => setTab('range')}
              className={`flex-1 py-1.5 border-l border-gray-200 transition-colors ${tab === 'range' ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              style={tab === 'range' ? { backgroundColor: ORANGE } : {}}
              data-testid="b29-range-tab"
            >
              Date range
            </button>
          </div>

          {tab === 'month' ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <button type="button" onClick={() => setPickerYear((y) => y - 1)} className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm font-bold text-gray-800">{pickerYear}</span>
                <button type="button" onClick={() => setPickerYear((y) => y + 1)} className="p-1 rounded hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-3 gap-1 mb-3">
                {MONTH_NAMES.map((name, m) => {
                  const active = range.kind === 'month' && range.year === pickerYear && range.month === m;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => pickMonth(m)}
                      className="py-1.5 text-xs rounded-lg font-semibold transition-colors"
                      style={{ background: active ? ORANGE : '#f3f4f6', color: active ? 'white' : '#374151' }}
                      data-testid={`b29-month-${m}`}
                    >
                      {name.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => { onChange(makeMonthRange(now.getFullYear(), now.getMonth())); setOpen(false); }}
                className="w-full py-1.5 rounded-lg border-2 text-xs font-bold"
                style={{ borderColor: ORANGE, color: ORANGE }}
                data-testid="b29-this-month"
              >
                Back to this month
              </button>
            </>
          ) : (
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-gray-600">From</Label>
                <input type="date" value={fromVal} onChange={(e) => setFromVal(e.target.value)} className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm mt-0.5 focus:border-orange-400 focus:outline-none" data-testid="b29-range-from" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">To</Label>
                <input type="date" value={toVal} onChange={(e) => setToVal(e.target.value)} className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm mt-0.5 focus:border-orange-400 focus:outline-none" data-testid="b29-range-to" />
              </div>
              <button
                type="button"
                onClick={applyRange}
                className="w-full py-2 rounded-lg text-white text-sm font-semibold"
                style={{ backgroundColor: ORANGE }}
                data-testid="b29-range-apply"
              >
                Apply range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Slide-out "View Price Lists" panel (same look and behaviour as Customer Management's Pricing panel) ─────────────

const PriceListPanel = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`${B29_URL}/catalog/suppliers`);
        if (alive) setSuppliers(res.data?.suppliers || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || 'Failed to load the B29 price lists');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const openSupplier = async (supplier) => {
    setSelected(supplier);
    setCatalog(null);
    setCatalogLoading(true);
    try {
      const res = await api.get(`${B29_URL}/catalog/suppliers/${supplier.id}/products`);
      setCatalog(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load this supplier\'s products');
      setSelected(null);
    } finally {
      setCatalogLoading(false);
    }
  };

  const backToSuppliers = () => { setSelected(null); setCatalog(null); };

  return (
    <div className="h-full flex flex-col" data-testid="b29-pricelist-panel">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
        {selected && (
          <button onClick={backToSuppliers} className="text-gray-400 hover:text-gray-700 rounded-full p-1 hover:bg-gray-100 flex-shrink-0" title="Back to all suppliers" data-testid="b29-pricelist-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
        >
          <PoundSterling className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-extrabold text-gray-900 leading-tight truncate">{selected ? selected.name : 'B29 Price Lists'}</h3>
          <p className="text-[11px] text-gray-500 truncate">
            {selected ? 'All products by category' : `${suppliers.length} supplier${suppliers.length === 1 ? '' : 's'} — click one to see its products`}
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
        {!selected && (
          loading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading suppliers…</span>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-sm text-red-600">{error}</div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <PoundSterling className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-sm">No B29 price lists have been loaded yet.</p>
            </div>
          ) : (
            suppliers.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => openSupplier(s)}
                className="w-full flex items-center gap-3 rounded-xl border-2 border-orange-200 bg-white px-4 py-3 text-left hover:bg-orange-50 hover:border-orange-400 transition-colors"
                data-testid={`b29-pricelist-supplier-${s.id}`}
              >
                <span className="flex-1 min-w-0 font-bold text-sm text-gray-900 truncate">{s.name}</span>
                <span className="text-[11px] font-semibold bg-orange-100 text-orange-700 rounded-full px-2 py-0.5 flex-shrink-0">
                  {s.product_count} product{s.product_count === 1 ? '' : 's'}
                </span>
                <ChevronRight className="w-4 h-4 text-orange-400 flex-shrink-0" />
              </button>
            ))
          )
        )}

        {selected && (
          catalogLoading || !catalog ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading products…</span>
            </div>
          ) : (catalog.categories || []).length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">No products on file for this supplier.</div>
          ) : (
            catalog.categories.map((cat) => (
              <section key={cat.id} className="rounded-xl border border-gray-200 overflow-hidden bg-white" data-testid={`b29-pricelist-category-${cat.id}`}>
                <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: ORANGE }}>
                  <span className="text-sm font-bold text-white uppercase tracking-wide truncate">{cat.name}</span>
                  <span className="text-xs text-white/80 ml-auto flex-shrink-0">{cat.products.length} product{cat.products.length === 1 ? '' : 's'}</span>
                </div>
                {cat.products.map((p) => {
                  const code = p.stock_code || p.supplier_product_code || '—';
                  const tiers = (p.tiers || []).filter((t) => t.price !== null && t.price !== undefined);
                  const detailBits = [p.dimensions, p.thickness_mm ? `${p.thickness_mm}mm` : null, p.pallet_qty ? `Pallet: ${p.pallet_qty}` : null].filter(Boolean);
                  return (
                    <div key={p.id} className="grid grid-cols-[130px_minmax(0,1fr)_140px] gap-4 items-center px-4 py-3 border-t border-gray-100 hover:bg-orange-50/40">
                      <div className="font-mono font-bold text-sm text-gray-900 break-all">{code}</div>
                      <div className="min-w-0 flex items-center gap-4 flex-wrap">
                        <p className="text-sm text-gray-800">{p.description || '—'}</p>
                        {detailBits.length > 0 && (
                          <p className="text-xs text-gray-400 whitespace-nowrap">{detailBits.join(' · ')}</p>
                        )}
                        {tiers.length > 1 && (
                          <p className="text-xs text-amber-700 whitespace-nowrap">
                            {tiers.map((t) => `${t.label}: ${money(t.price)}`).join(' · ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-base font-bold text-gray-900 tabular-nums">{money(p.best_price)}</div>
                        <div className="text-xs text-gray-400">per {p.price_uom || p.order_uom || 'Each'}</div>
                      </div>
                    </div>
                  );
                })}
              </section>
            ))
          )
        )}
      </div>
    </div>
  );
};

// ── "Build from Recent Orders" picker (inline in the main modal) ────────────────────────────────────────────────────

// The ticked lines (selectedItems / setSelectedItems) live in the B29 modal itself, so the orange Continue button can sit in
// the modal header and always be visible, wherever you are in the list.
const RecentOrdersPicker = ({ selectedItems, setSelectedItems }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${API}/api/automations/emails`, { params: { email_type: 'order', limit: 60 } });
        setOrders(res.data?.emails || []);
      } catch {
        toast.error('Failed to load recent orders');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getItems = (o) => o.product_lines || [];

  const toggleItem = (order, item, idx) => {
    const key = `${order.id}-${idx}`;
    setSelectedItems((prev) => (prev.some((p) => p.key === key)
      ? prev.filter((p) => p.key !== key)
      : [...prev, {
        key,
        product_code: item.product_code || item.code || '',
        description: item.description || item.product_name || '',
        quantity: parseFloat(item.qty || item.quantity || 1),
        price: 0,
        order_no: order.order_number || '',
        customer_name: order.customer_name || '',
        delivery_address: order.delivery_address || '',
      }]));
  };

  const filtered = orders.filter((o) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (o.customer_name || '').toLowerCase().includes(s) || (o.order_number || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-3" data-testid="b29-recent-orders-view">
      <div className={fieldCardClass}>
        <p className="text-xs text-gray-500 mb-2">Pick the sales order lines that need stock transferred from B29 — tick each line you want, then continue.</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <Input placeholder="Search by customer or order number..." value={search} onChange={(e) => setSearch(e.target.value)} className={`pl-8 ${inputClass}`} data-testid="b29-recent-orders-search" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-gray-400 py-8">No recent sales orders found.</p>
      ) : (
        <div className="space-y-2">
          {filtered.slice(0, 40).map((o, oi) => {
            const oKey = o.id || oi;
            const lines = getItems(o);
            const isOpen = expanded === oKey;
            return (
              <div key={oKey} className="border-2 border-orange-200 rounded-2xl overflow-hidden bg-white" data-testid={`b29-recent-order-${oi}`}>
                <button
                  className="w-full flex items-center justify-between p-3 hover:bg-orange-50 text-left"
                  onClick={() => setExpanded(isOpen ? null : oKey)}
                >
                  <div className="min-w-0">
                    <span className="font-semibold text-sm">{o.customer_name || o.from_email || 'Unknown customer'}</span>
                    <span className="text-xs text-gray-400 ml-2">#{o.order_number || '—'}</span>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{lines.length} item(s)</span>
                </button>
                {isOpen && (
                  <div className="border-t p-2 space-y-1 bg-orange-50/40">
                    {lines.map((item, idx) => {
                      const key = `${oKey}-${idx}`;
                      const checked = selectedItems.some((p) => p.key === key);
                      return (
                        <label key={idx} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white cursor-pointer text-sm" data-testid={`b29-recent-order-item-${oi}-${idx}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleItem(o, item, idx)} className="accent-orange-500" />
                          <span className="font-medium">{item.product_code || item.code || '—'}</span>
                          <span className="text-gray-500 truncate flex-1">{item.description || item.product_name}</span>
                          <span className="text-gray-400">qty {item.qty || item.quantity || 1}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

// ── New / Edit Transfer page (inline in the main modal, so the Price Lists panel can stay open beside it) ───────────

const TransferBuilder = ({ initial, branch, person, onSaved, onCancel, onRefresh, onEmail, onDraft }) => {
  const [recordId, setRecordId] = useState(initial?.id || null);
  const [status, setStatus] = useState(initial?.status || 'current');
  const [date, setDate] = useState(initial?.date || todayIso());
  const [reqDate, setReqDate] = useState(initial?.req_date || '');
  const [orderNo, setOrderNo] = useState(initial?.order_no || '');
  const [customerName, setCustomerName] = useState(initial?.customer_name || '');
  const [deliveryAddress, setDeliveryAddress] = useState(initial?.delivery_address || '');
  // 'auto' = follows the sales order; 'manual' = typed / edited / cleared by the user; 'collection' = Collection is selected.
  // An existing transfer brings its own source with it; a new one (blank, or built from Recent Orders) starts on 'auto'.
  const [addressSource, setAddressSource] = useState(
    initial?.delivery_address_source || (initial?.id ? (initial?.delivery_address ? 'manual' : 'auto') : 'auto'),
  );
  const [items, setItems] = useState(() => {
    const real = (initial?.items || []).filter((i) => !i.is_carriage);
    return real.length ? real.map((i) => ({ ...i, id: Date.now() + Math.random() })) : [emptyLine()];
  });
  const [carriageMode, setCarriageMode] = useState(initial?.carriage_mode || 'auto');
  const [manualFee, setManualFee] = useState(initial?.carriage_manual_fee ? String(initial.carriage_manual_fee) : '');
  const [carriage, setCarriage] = useState(null);
  const [busy, setBusy] = useState('');
  const lookupCost = useB29CostLookup();

  // "Bring into our stock": the stock is coming IN to the user's own branch, so the delivery address becomes that branch's address.
  const [stockIn, setStockIn] = useState(!!initial?.stock_in);
  const [stockInBusy, setStockInBusy] = useState(false);
  const [stockInBranch, setStockInBranch] = useState('');
  const stockInInfoRef = useRef(null);      // the branch address, once fetched
  const preStockAddressRef = useRef(null);  // what the address was before the toggle was switched on

  // Tells the B29 modal what is on this page right now, so an unfinished transfer can sit in the Open bar and be reopened
  // from exactly this point.
  useEffect(() => {
    if (!onDraft) return;
    onDraft({
      id: recordId,
      status,
      date,
      req_date: reqDate,
      order_no: orderNo,
      customer_name: customerName,
      delivery_address: deliveryAddress,
      delivery_address_source: addressSource,
      stock_in: stockIn,
      carriage_mode: carriageMode,
      carriage_manual_fee: parseFloat(manualFee) || 0,
      items,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, status, date, reqDate, orderNo, customerName, deliveryAddress, addressSource, stockIn, carriageMode, manualFee, items]);

  // Auto-fill cost prices from the B29 master catalog for any lines that
  // arrived with a product code but no price (e.g. picked from a recent order).
  useEffect(() => {
    (async () => {
      const toResolve = items.filter((it) => it.product_code && (!it.price || it.price === 0));
      for (const line of toResolve) {
        const result = await lookupCost(line.product_code, line.quantity);
        if (result) {
          setItems((prev) => prev.map((it) => (it.id === line.id ? {
            ...it,
            price: result.price,
            description: it.description || result.description,
            supplier: result.supplier || it.supplier,
            tier_label: result.tier_label || '',
          } : it)));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carriage updates by itself as lines, dates or the carriage mode change. There is only ever ONE carriage charge per
  // transfer, however many suppliers are on it.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await api.post(`${B29_URL}/carriage/calculate`, {
          items: items.filter((i) => i.product_code || i.description).map(toApiItem),
          date,
          req_date: reqDate || null,
          carriage_mode: carriageMode,
          carriage_manual_fee: parseFloat(manualFee) || 0,
        });
        if (!cancelled) setCarriage(res.data?.carriage || null);
      } catch {
        // Not fatal — the carriage is calculated again, for real, when the transfer is saved.
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [items, date, reqDate, carriageMode, manualFee]);

  // ── Delivery address ─────────────────────────────────────────────────────────────────────────────────────────────
  // addressSource 'auto'       -> follows the sales order for the order number above and refreshes by itself
  //               'manual'     -> typed / edited / cleared by the user: the system never overwrites it again
  //               'collection' -> the carriage is set to Collection, so the address is just COLLECTION
  const preCollectionAddressRef = useRef(null);
  const prevCarriageModeRef = useRef(carriageMode);

  const lookupOrderAddress = async (orderNumber) => {
    const res = await api.get(`${B29_URL}/order-lookup/${encodeURIComponent(orderNumber)}`);
    return res.data?.found ? res.data : null;
  };

  // An order number is typed, picked from Recent Orders, or an existing transfer is opened: fill the delivery address
  // from that sales order (only while the address is still following the order).
  useEffect(() => {
    if (addressSource !== 'auto') return undefined;
    const number = orderNo.trim();
    if (!number) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const order = await lookupOrderAddress(number);
        if (cancelled || !order) return;
        setDeliveryAddress(order.delivery_address || '');
        setCustomerName((prev) => prev || order.customer_name || '');
      } catch {
        // Not fatal: the address can still be typed in by hand.
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNo, addressSource]);

  // Carriage switched to Collection: the address becomes COLLECTION. Switched back: the previous address comes back.
  useEffect(() => {
    const previous = prevCarriageModeRef.current;
    prevCarriageModeRef.current = carriageMode;
    if (previous === carriageMode) return;
    if (carriageMode === 'collection' && !stockIn) {
      preCollectionAddressRef.current = { address: deliveryAddress, source: addressSource };
      setDeliveryAddress(COLLECTION_ADDRESS);
      setAddressSource('collection');
    } else if (previous === 'collection' && addressSource === 'collection') {
      const before = preCollectionAddressRef.current;
      preCollectionAddressRef.current = null;
      if (before && before.source === 'manual') {
        setDeliveryAddress(before.address);
        setAddressSource('manual');
      } else {
        setDeliveryAddress(before && before.source === 'auto' ? before.address : '');
        setAddressSource('auto');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carriageMode]);

  // Typing in, editing or clearing the box means the user now owns the address.
  const onAddressChange = (value) => {
    setDeliveryAddress(value);
    setAddressSource('manual');
  };

  const refreshAddressFromOrder = async () => {
    const number = orderNo.trim();
    if (!number) { toast.error('Enter the customer order number first'); return; }
    if (stockIn) {
      toast.info('Bring into our stock is on, so the address is your branch. Switch it off to load the order\'s delivery address.');
      return;
    }
    if (carriageMode === 'collection') {
      toast.info('Collection is selected, so the address is COLLECTION. Switch the carriage back to Auto or Manual to load the order\'s delivery address.');
      return;
    }
    try {
      const order = await lookupOrderAddress(number);
      if (!order) { toast.error('That order number is not in the system, so there is no delivery address to load'); return; }
      setDeliveryAddress(order.delivery_address || '');
      setAddressSource('auto');
      setCustomerName((prev) => prev || order.customer_name || '');
      toast.success('Delivery address loaded from the order');
    } catch {
      toast.error('Could not look up that order');
    }
  };

  // "Bring into our stock": switching it on puts IN TO OUR STOCK and the user's own branch address in the delivery address box;
  // switching it off puts back whatever was there before.
  const toggleStockIn = async () => {
    if (stockIn) {
      setStockIn(false);
      const before = preStockAddressRef.current;
      preStockAddressRef.current = null;
      if (carriageMode === 'collection') {
        setDeliveryAddress(COLLECTION_ADDRESS);
        setAddressSource('collection');
      } else if (before && before.source !== 'collection') {
        setDeliveryAddress(before.address);
        setAddressSource(before.source);
      } else {
        setDeliveryAddress('');
        setAddressSource('auto');
      }
      return;
    }
    setStockInBusy(true);
    try {
      let info = stockInInfoRef.current;
      if (!info) {
        const res = await api.get(`${B29_URL}/my-branch-address`);
        info = res.data;
        if (info?.found) stockInInfoRef.current = info;
      }
      if (!info?.found) {
        toast.error(`There is no address on file for branch ${info?.branch_code || 'your branch'}. Add it in Settings > Branch Directory.`);
        return;
      }
      preStockAddressRef.current = { address: deliveryAddress, source: addressSource };
      setDeliveryAddress(`${STOCK_IN_LABEL}\n${info.address}`);
      setAddressSource('manual');
      setStockInBranch(info.branch_code || '');
      setStockIn(true);
    } catch {
      toast.error('Could not load your branch address');
    } finally {
      setStockInBusy(false);
    }
  };

  const addressStatusText = stockIn
    ? `Bringing the stock IN to our stock${stockInBranch ? ` (${stockInBranch})` : ''}: this is your branch address`
    : addressSource === 'collection'
    ? 'Collection selected'
    : addressSource === 'manual'
      ? 'Edited by hand: it will not change when the order changes'
      : (orderNo.trim() ? 'Filled in automatically from the order. Edit it to keep your own version.' : 'Enter the order number to fill this in automatically');

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const resolveCost = async (idx) => {
    const line = items[idx];
    if (!line) return;
    const result = await lookupCost(line.product_code, line.quantity);
    if (result) {
      setItems((prev) => prev.map((it, i) => (i === idx ? {
        ...it,
        price: result.price,
        description: it.description || result.description,
        supplier: result.supplier || it.supplier,
        tier_label: result.tier_label || '',
      } : it)));
      toast.success(`Cost price filled from B29 catalog: £${result.price.toFixed(2)} (${result.tier_label || 'Standard'})`);
    }
  };

  const addLine = () => setItems((prev) => [...prev, emptyLine()]);
  const removeLine = (idx) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.price) || 0), 0);
  const carriageFee = carriage ? carriage.fee || 0 : 0;

  /** Saves (creates or updates) the transfer and returns the saved record, or null if something was missing/failed. */
  const persist = async () => {
    if (!branch.trim()) { toast.error('Branch is required'); return null; }
    if (!person.trim()) { toast.error('Person is required'); return null; }
    const validItems = items.filter((i) => i.product_code || i.description);
    if (validItems.length === 0) { toast.error('Add at least one line item'); return null; }
    try {
      const calc = await api.post(`${B29_URL}/carriage/calculate`, {
        items: validItems.map(toApiItem),
        date,
        req_date: reqDate || null,
        carriage_mode: carriageMode,
        carriage_manual_fee: parseFloat(manualFee) || 0,
      });
      const finalItems = calc.data?.items || [];
      if (finalItems.length > 29) { toast.error('Maximum 29 line items per B29 transfer form (including the carriage line)'); return null; }
      const payload = {
        date,
        req_date: reqDate,
        branch,
        person,
        order_no: orderNo,
        customer_name: customerName,
        delivery_address: deliveryAddress,
        delivery_address_source: addressSource,
        stock_in: stockIn,
        carriage_mode: carriageMode,
        carriage_manual_fee: carriageMode === 'manual' ? (parseFloat(manualFee) || 0) : 0,
        items: finalItems.map(toApiItem),
      };
      const res = recordId
        ? await api.put(`${B29_URL}/${recordId}`, payload)
        : await api.post(B29_URL, payload);
      setRecordId(res.data.id);
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save transfer');
      return null;
    }
  };

  const saveAndClose = async () => {
    setBusy('save');
    try {
      const saved = await persist();
      if (saved) {
        toast.success(recordId ? 'B29 transfer updated' : 'B29 transfer created');
        onSaved();
      }
    } finally {
      setBusy('');
    }
  };

  // Download / Print / Email / Mark Completed: each saves the transfer first, so the action always works on what is on screen.
  const runAction = async (kind) => {
    if ((kind === 'excel' || kind === 'email' || kind === 'print') && !deliveryAddress.trim()) {
      toast.warning('No delivery address is set on this transfer — please add and check it before dispatch.');
    }
    setBusy(kind);
    try {
      const saved = await persist();
      if (!saved) return;
      if (kind === 'excel') {
        await downloadTransferExcel(saved);
        onRefresh();
      } else if (kind === 'print') {
        printTransfer(saved);
        onRefresh();
      } else if (kind === 'email') {
        onEmail(saved);
        onRefresh();
      } else if (kind === 'complete') {
        const completing = status !== 'completed';
        try {
          await api.patch(`${B29_URL}/${saved.id}/${completing ? 'complete' : 'reopen'}`);
          setStatus(completing ? 'completed' : 'current');
          toast.success(completing ? 'Marked Completed' : 'Moved back to Current');
          onSaved();
        } catch {
          toast.error('Failed to update status');
        }
      }
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-2" data-testid="b29-transfer-builder">
      <div className="flex flex-col md:flex-row gap-2 items-stretch">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-2.5 flex-1">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-orange-700">Customer Order No</Label>
              <Input
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                className="mt-1 rounded-xl border-2 border-orange-400 bg-orange-50/40 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 h-9"
                data-testid="b29-builder-order-no"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Requested Delivery</Label>
              <Input type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} className={`mt-1 ${inputClass} h-9`} data-testid="b29-builder-req-date" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`mt-1 ${inputClass} h-9`} data-testid="b29-builder-date" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Customer Name</Label>
              <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className={`mt-1 ${inputClass} h-9`} data-testid="b29-builder-customer-name" />
            </div>
          </div>
        </div>

        <div
          className={`rounded-xl border-2 shadow-sm p-2.5 flex-1 flex flex-col ${deliveryAddress.trim() ? 'border-orange-200 bg-white' : 'border-amber-300 bg-amber-50'}`}
          data-testid="b29-builder-delivery-address-card"
        >
          <button
            type="button"
            role="switch"
            aria-checked={stockIn}
            onClick={toggleStockIn}
            disabled={stockInBusy}
            className="flex items-center gap-2 mb-1.5 text-left disabled:opacity-60"
            data-testid="b29-builder-stock-in-toggle"
          >
            <span
              className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${stockIn ? '' : 'bg-gray-300'}`}
              style={stockIn ? { backgroundColor: ORANGE } : undefined}
            >
              <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${stockIn ? 'translate-x-4' : ''}`} />
            </span>
            <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Bring into our stock</span>
            {stockInBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" /> : null}
          </button>
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Delivery Address</Label>
            <button
              type="button"
              onClick={refreshAddressFromOrder}
              className="text-[11px] font-bold text-orange-700 hover:underline"
              title="Load the delivery address from the sales order again (replaces what is in the box)"
              data-testid="b29-builder-refresh-address"
            >
              Refresh from order
            </button>
          </div>
          <p className="text-[10px] text-gray-500 leading-tight" data-testid="b29-builder-address-status">{addressStatusText}</p>
          <textarea
            value={deliveryAddress}
            onChange={(e) => onAddressChange(e.target.value)}
            rows={3}
            placeholder="Full delivery address — this is what goes on the B29 Excel form and tells the warehouse where to send the goods"
            className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none resize-none mt-1 flex-1"
            data-testid="b29-builder-delivery-address"
          />
        </div>
      </div>

      <div className={fieldCardClass}>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Line Items ({items.length}/28 + carriage)</Label>
          <button onClick={addLine} className={`${headerBtnClass} border-orange-400 text-orange-700 hover:bg-orange-50`} data-testid="b29-builder-add-line">
            <Plus className="w-3.5 h-3.5" /> Add Line
          </button>
        </div>
        <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: '#fed7aa' }}>
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: ORANGE }}>
                <TableHead className="text-white">Product Code</TableHead>
                <TableHead className="text-white">Description</TableHead>
                <TableHead className="text-white w-20">Qty</TableHead>
                <TableHead className="text-white w-24">Price (£)</TableHead>
                <TableHead className="text-white w-24 text-right">Total</TableHead>
                <TableHead className="text-white w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={item.id} data-testid={`b29-builder-line-${idx}`}>
                  <TableCell className="align-top">
                    <Input
                      value={item.product_code}
                      onChange={(e) => updateItem(idx, 'product_code', e.target.value)}
                      onBlur={() => resolveCost(idx)}
                      className="h-9 text-sm rounded-xl border-2 border-gray-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                      data-testid={`b29-builder-line-code-${idx}`}
                    />
                  </TableCell>
                  <TableCell className="align-top">
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      className="h-9 text-sm rounded-xl border-2 border-gray-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                      data-testid={`b29-builder-line-desc-${idx}`}
                    />
                    {(item.supplier || item.tier_label) && (
                      <p className="text-[10px] text-amber-700 mt-0.5 truncate">
                        {item.supplier ? <span className="font-semibold">{item.supplier}</span> : null}
                        {item.supplier && item.tier_label ? ' · ' : ''}
                        {item.tier_label ? `Tier: ${item.tier_label}` : ''}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                      onBlur={() => resolveCost(idx)}
                      className="h-9 text-sm rounded-xl border-2 border-gray-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                      data-testid={`b29-builder-line-qty-${idx}`}
                    />
                  </TableCell>
                  <TableCell className="align-top">
                    <Input
                      type="number"
                      step="0.01"
                      value={item.price}
                      onChange={(e) => updateItem(idx, 'price', e.target.value)}
                      className="h-9 text-sm rounded-xl border-2 border-gray-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                      data-testid={`b29-builder-line-price-${idx}`}
                    />
                  </TableCell>
                  <TableCell className="align-top text-right text-sm font-semibold tabular-nums pt-3.5">
                    {money((parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0))}
                  </TableCell>
                  <TableCell className="align-top pt-3.5">
                    <button onClick={() => removeLine(idx)} className="p-1 text-red-400 hover:bg-red-50 rounded-full" data-testid={`b29-builder-line-remove-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className={fieldCardClass} data-testid="b29-builder-carriage">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Carriage</Label>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-xl border-2 border-orange-300 overflow-hidden text-xs font-bold">
              {[
                { id: 'auto', label: 'Auto' },
                { id: 'manual', label: 'Manual £' },
                { id: 'collection', label: 'Collection' },
              ].map((opt, n) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setCarriageMode(opt.id)}
                  className={`px-3 py-1.5 transition-colors ${n > 0 ? 'border-l-2 border-orange-300' : ''} ${carriageMode === opt.id ? 'text-white' : 'text-orange-700 bg-white hover:bg-orange-50'}`}
                  style={carriageMode === opt.id ? { backgroundColor: ORANGE } : {}}
                  data-testid={`b29-carriage-mode-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="text-sm font-extrabold tabular-nums" style={{ color: ORANGE }} data-testid="b29-carriage-summary">
              {carriage ? (carriage.fee > 0 ? money(carriage.fee) : 'FOC') : '…'}
            </span>
          </div>
        </div>
        {carriageMode === 'manual' && (
          <div className="mt-2 max-w-[200px]">
            <Label className="text-xs text-gray-600">Carriage charge (£)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={manualFee}
              onChange={(e) => setManualFee(e.target.value)}
              className={`mt-1 ${inputClass}`}
              data-testid="b29-carriage-manual-fee"
            />
          </div>
        )}
      </div>

      <div className={`${fieldCardClass} flex flex-wrap items-center justify-between gap-3`} data-testid="b29-builder-totals-actions">
        <div className="flex items-center gap-4 text-sm flex-wrap" data-testid="b29-builder-totals">
          <span className="text-gray-500">Lines <b className="text-gray-800 tabular-nums">{money(subtotal)}</b></span>
          <span className="text-gray-500">Carriage <b className="text-gray-800 tabular-nums">{money(carriageFee)}</b></span>
          <span className="text-gray-500">Total <b className="tabular-nums" style={{ color: ORANGE }}>{money(subtotal + carriageFee)}</b></span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => runAction('excel')}
            disabled={!!busy}
            className={`${actionBtnClass} border-green-500 text-green-700 bg-white hover:bg-green-50`}
            data-testid="b29-builder-excel-btn"
          >
            {busy === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Excel
          </button>
          <button
            onClick={() => runAction('print')}
            disabled={!!busy}
            className={`${actionBtnClass} border-slate-400 text-slate-700 bg-white hover:bg-slate-50`}
            data-testid="b29-builder-print-btn"
          >
            {busy === 'print' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Print
          </button>
          <button
            onClick={() => runAction('email')}
            disabled={!!busy}
            className={`${actionBtnClass} border-blue-500 text-blue-700 bg-white hover:bg-blue-50`}
            data-testid="b29-builder-email-btn"
          >
            {busy === 'email' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email
          </button>
          <button
            onClick={() => runAction('complete')}
            disabled={!!busy}
            className={`${actionBtnClass} ${status === 'completed' ? 'border-emerald-500 text-emerald-700 bg-white hover:bg-emerald-50' : 'border-emerald-600 text-white bg-emerald-600 hover:bg-emerald-700'}`}
            data-testid="b29-builder-complete-btn"
          >
            {busy === 'complete' ? <Loader2 className="w-4 h-4 animate-spin" /> : (status === 'completed' ? <RotateCcw className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />)}
            {status === 'completed' ? 'Reopen' : 'Mark Completed'}
          </button>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} disabled={!!busy} className={`${actionBtnClass} h-11 px-5 border-gray-300 text-gray-700 bg-white`} data-testid="b29-builder-cancel-btn">
          Cancel
        </button>
        <button
          onClick={saveAndClose}
          disabled={!!busy}
          className={`${actionBtnClass} h-11 px-6 text-white border-orange-500`}
          style={{ backgroundColor: ORANGE }}
          data-testid="b29-builder-save-btn"
        >
          {busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Transfer
        </button>
      </div>
    </div>
  );
};

// ── One transfer card ───────────────────────────────────────────────────────────────────────────────────────────────

const TransferCard = ({ transfer, onChanged, onEdit, onEmail }) => {
  const [busy, setBusy] = useState(false);
  const completed = transfer.status === 'completed';
  const suppliers = transferSuppliers(transfer);

  const toggleComplete = async () => {
    setBusy(true);
    try {
      await api.patch(`${B29_URL}/${transfer.id}/${completed ? 'reopen' : 'complete'}`);
      toast.success(completed ? 'Moved back to Current' : 'Marked Completed');
      onChanged();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this B29 transfer?')) return;
    try {
      await api.delete(`${B29_URL}/${transfer.id}`);
      toast.success('Deleted');
      onChanged();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div
      className="rounded-2xl border-2 bg-white hover:shadow-lg transition-all duration-200 p-3 flex flex-col gap-2 min-w-0"
      style={{ borderColor: completed ? '#bbf7d0' : '#fed7aa' }}
      data-testid={`b29-transfer-card-${transfer.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onEdit(transfer)} title="Open this transfer">
          <div className="flex items-center gap-2">
            <p className="font-extrabold text-sm text-gray-900 truncate">
              {transfer.branch} <span className="text-gray-400 font-normal">· {transfer.transfer_ref}</span>
            </p>
            <span
              className={`text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 ${completed ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}
            >
              {completed ? 'Completed' : 'Current'}
            </span>
          </div>
          <p className="text-xs text-gray-700 mt-0.5 truncate">
            {transfer.order_no ? <span className="font-semibold">Order {transfer.order_no}</span> : <span className="text-gray-400">No order number</span>}
            {transfer.customer_name ? <span> · {transfer.customer_name}</span> : null}
          </p>
          <p className="text-sm font-extrabold text-gray-900 mt-1" data-testid={`b29-transfer-req-date-${transfer.id}`}>
            Requested delivery: {fmtDMY(transfer.req_date) || <span className="font-normal text-gray-400">not set</span>}
          </p>
          <p className="text-[11px] mt-0.5 truncate" data-testid={`b29-transfer-delivery-address-${transfer.id}`}>
            {transfer.delivery_address ? (
              <span className="text-gray-600"><span className="font-semibold text-gray-700">Deliver to:</span> {transfer.delivery_address}</span>
            ) : (
              <span className="font-bold text-red-600">No delivery address set — check before dispatch</span>
            )}
          </p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {suppliers.length === 0 ? (
              <span className="text-[10px] text-gray-400">No supplier on the lines</span>
            ) : suppliers.map((name) => (
              <span key={name} className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200">
                {name}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            {fmtDMY(transfer.date)} · {transfer.person} · {(transfer.items || []).length} line item(s)
          </p>
        </div>
        <button onClick={remove} className="p-1.5 rounded-full hover:bg-red-50 text-red-400 flex-shrink-0" title="Delete" data-testid={`b29-transfer-delete-${transfer.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        <button
          onClick={() => downloadTransferExcel(transfer)}
          className={`${actionBtnClass} h-9 px-1 border-green-500 text-green-700 bg-white hover:bg-green-50`}
          data-testid={`b29-transfer-download-${transfer.id}`}
        >
          <Download className="w-3.5 h-3.5" /> Excel
        </button>
        <button
          onClick={() => printTransfer(transfer)}
          className={`${actionBtnClass} h-9 px-1 border-slate-400 text-slate-700 bg-white hover:bg-slate-50`}
          data-testid={`b29-transfer-print-${transfer.id}`}
        >
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
        <button
          onClick={() => onEmail(transfer)}
          className={`${actionBtnClass} h-9 px-1 border-blue-500 text-blue-700 bg-white hover:bg-blue-50`}
          data-testid={`b29-transfer-email-${transfer.id}`}
        >
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
        <button
          onClick={toggleComplete}
          disabled={busy}
          className={`${actionBtnClass} h-9 px-1 ${completed ? 'border-emerald-500 text-emerald-700 bg-white hover:bg-emerald-50' : 'border-emerald-600 text-white bg-emerald-600 hover:bg-emerald-700'}`}
          data-testid={`b29-transfer-toggle-${transfer.id}`}
        >
          {completed ? <RotateCcw className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {completed ? 'Reopen' : 'Complete'}
        </button>
      </div>
    </div>
  );
};

/** The B29 modal — styled exactly like the Customer Management modal: same width, same header, a "View Price Lists" tab
 * down the left edge that slides a panel out on the right, and (below the sub-header) full height. New / Edit Transfer
 * opens INSIDE this modal, in place of the transfer list, so the Price Lists panel can stay open next to it. */
const B29Modal = ({ isOpen, onClose, user: userProp = null, resumeSnapshot = null }) => {
  // Reopened from the Open bar, the page that first opened this modal may be gone, so the user comes along in the snapshot.
  const user = userProp || resumeSnapshot?.user || null;
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const [range, setRange] = useState(currentMonthRange);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Always opens on the main list (this month's transfers). Only a transfer reopened from the Open bar starts part way through.
  const [view, setView] = useState(resumeSnapshot?.view || 'list'); // 'list' | 'choose' | 'orders' | 'builder'
  const [builderInitial, setBuilderInitial] = useState(resumeSnapshot?.draft || null);
  const [builderKey, setBuilderKey] = useState(0);
  const [builderBranch, setBuilderBranch] = useState(resumeSnapshot?.builderBranch || '');
  const [builderPerson, setBuilderPerson] = useState(resumeSnapshot?.builderPerson || '');
  const [draft, setDraft] = useState(resumeSnapshot?.draft || null);              // what is on the New / Edit Transfer page right now
  const [pickedLines, setPickedLines] = useState(resumeSnapshot?.pickedLines || []); // the ticked Recent Orders lines
  const activityIdRef = useRef(resumeSnapshot?.activityId || null);
  const [panelOpen, setPanelOpen] = useState(false);
  const v3w = useV3Window(isOpen, panelOpen ? 1600 : 1040);
  const [emailTransfer, setEmailTransfer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(B29_URL, { params: { status: 'all', date_from: range.from, date_to: range.to } });
      setTransfers(res.data?.transfers || []);
    } catch {
      toast.error('Failed to load B29 transfers');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  // Same as Customer Management: while open, the page's own scroll is locked so it can't scroll behind the modal.
  useEffect(() => {
    if (!isOpen || !modalUiV2) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen, modalUiV2]);

  // Open bar: a transfer that is part way through (picking order lines, or on the New / Edit Transfer page) is an "Open
  // Activity". Leaving the page or closing the modal puts it in the Open bar, and clicking it there reopens it from this point.
  const inProgress = view === 'orders' || view === 'builder';
  const activityTitle = `(B29) - ${(draft && draft.customer_name) || (view === 'orders' ? 'Picking order lines' : 'New Transfer')}`;
  const buildSnapshot = () => ({
    activityId: activityIdRef.current,
    view,
    draft,
    pickedLines,
    builderBranch,
    builderPerson,
    user: user ? { full_name: user.full_name, username: user.username, email: user.email, branch_id: user.branch_id } : null,
  });

  useEffect(() => {
    if (!isOpen || !modalUiV2 || !inProgress || activityIdRef.current) return;
    activityIdRef.current = `b29-${Date.now()}`;
    activityBar.openActivity({
      type: 'b29_transfer',
      activityId: activityIdRef.current,
      title: activityTitle,
      subtitle: 'B29 Transfer',
      snapshot: buildSnapshot(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, inProgress]);

  useEffect(() => {
    if (!isOpen || !modalUiV2 || !inProgress || !activityIdRef.current) return;
    activityBar.updateActiveSnapshot(buildSnapshot(), { title: activityTitle, subtitle: 'B29 Transfer' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, inProgress, view, draft, pickedLines, builderBranch, builderPerson]);

  // Back on the list / choose screen (cancelled or saved): nothing is part way through any more.
  useEffect(() => {
    if (inProgress || !modalUiV2 || !activityIdRef.current) return;
    activityBar.discardActive();
    activityIdRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress]);

  if (!isOpen) return null;

  // Closing with the red X part way through a transfer puts it in the Open bar instead of throwing it away.
  const handleClose = () => {
    if (inProgress && modalUiV2 && activityIdRef.current) {
      activityBar.minimizeActive();
      activityIdRef.current = null;
    }
    onClose?.();
  };

  const openBuilder = (initial) => {
    setBuilderInitial(initial);
    setBuilderBranch(initial?.branch || user?.branch_id || '');
    setBuilderPerson(initial?.person || user?.full_name || user?.username || user?.email || '');
    setBuilderKey((k) => k + 1);
    setView('builder');
  };
  const onOrdersPicked = (selectedItems) => {
    const distinctOrderNos = [...new Set(selectedItems.map((i) => i.order_no).filter(Boolean))];
    const distinctCustomers = [...new Set(selectedItems.map((i) => i.customer_name).filter(Boolean))];
    const distinctAddresses = [...new Set(selectedItems.map((i) => i.delivery_address).filter(Boolean))];
    openBuilder({
      items: selectedItems,
      order_no: distinctOrderNos.length === 1 ? distinctOrderNos[0] : '',
      customer_name: distinctCustomers.length === 1 ? distinctCustomers[0] : '',
      // Only pre-filled when every picked line agrees on the SAME address — if they differ, it is left blank so
      // nobody accidentally ships to the wrong place; the field must then be typed in and checked by hand.
      delivery_address: distinctAddresses.length === 1 ? distinctAddresses[0] : '',
    });
    setPickedLines([]);
  };
  const onSaved = () => { setView('list'); setBuilderInitial(null); setDraft(null); load(); };
  const goBack = () => {
    if (view === 'orders') { setPickedLines([]); setView('choose'); }
    else { setView('list'); setBuilderInitial(null); setDraft(null); }
  };

  const subtitle = {
    list: 'Stock transfers from the B29 warehouse',
    choose: 'New transfer — how do you want to start?',
    orders: 'New transfer — pick order lines',
    builder: builderInitial?.id ? 'Edit transfer' : 'New transfer',
  }[view];

  const now = new Date();
  const isThisMonth = range.kind === 'month' && range.year === now.getFullYear() && range.month === now.getMonth();

  return (
    <>
      <div
        className={`fixed left-0 right-0 bottom-0 flex items-stretch justify-center ${modalUiV2 ? 'p-3' : 'p-3 pt-8'} ${modalUiV2 ? 'bg-black/50' : 'bg-black/70 backdrop-blur-sm'} ${v3w.className}`}
        {...v3w.attrs}
        style={{ ...(modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 80 }), ...v3w.style }}
        data-testid="b29-modal-overlay"
      >
        {/* Shell: [tab rail] [B29 modal] [slide-out panel]. Runs from the sub-header to the bottom of the screen. */}
        <div className="flex items-stretch justify-center w-full h-full min-w-0" data-testid="b29-shell">

          {/* ── Tab rail — sits against the left edge of the modal ── */}
          <div className="hidden md:flex flex-col gap-2 pt-20 flex-shrink-0" style={{ width: RAIL_W }} data-testid="b29-tab-rail">
            <button
              type="button"
              onClick={() => setPanelOpen((v) => !v)}
              className={`flex flex-col items-center justify-center gap-2 py-4 rounded-l-2xl border-2 border-r-0 font-bold text-xs transition-colors ${
                panelOpen ? 'text-white border-orange-500' : 'text-orange-700 bg-orange-50 border-orange-200 hover:bg-orange-100'
              }`}
              style={{ width: RAIL_W, ...(panelOpen ? { backgroundColor: ORANGE } : {}) }}
              title="View Price Lists"
              data-testid="b29-tab-pricelists"
            >
              <PoundSterling className="w-4 h-4" />
              <span style={{ writingMode: 'vertical-rl', letterSpacing: '0.06em' }}>View Price Lists</span>
            </button>
          </div>

          {/* ── B29 modal ── */}
          <div
            className="bg-gradient-to-br from-orange-50/40 via-white to-orange-50/20 rounded-3xl shadow-2xl h-full w-full border-2 border-orange-300 flex flex-col overflow-hidden min-w-0"
            style={{
              maxWidth: panelOpen ? HALF_WIDTH : MODAL_MAX_W,
              flex: panelOpen ? `1 1 ${HALF_WIDTH}` : `0 1 ${MODAL_MAX_W}px`,
              transition: 'max-width 300ms ease, flex-basis 300ms ease',
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="b29-modal"
          >
            {/* Header */}
            <div className="v3-modal-bar flex items-center gap-3 px-5 py-3 border-b border-orange-100 bg-white/60 flex-shrink-0 flex-nowrap overflow-x-auto">
              <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
                {view !== 'list' && (
                  <button
                    onClick={goBack}
                    className="text-gray-400 hover:text-gray-700 rounded-full p-1 hover:bg-gray-100 flex-shrink-0"
                    data-testid="b29-back-btn"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                >
                  <Truck className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 tracking-tight leading-tight truncate max-w-[300px]">B29 Stock Transfers</h2>
                  <p className="text-[11px] text-gray-500 truncate">{subtitle}</p>
                </div>
              </div>

              {view === 'builder' && (
                <div className="flex-1 flex items-center justify-center gap-3 min-w-0" data-testid="b29-header-branch-person">
                  <div>
                    <Label className="text-[9px] font-bold uppercase tracking-wider text-gray-500">Branch</Label>
                    <Input
                      value={builderBranch}
                      onChange={(e) => setBuilderBranch(e.target.value)}
                      placeholder="e.g. C30"
                      className="h-9 w-24 text-sm rounded-xl border-2 border-gray-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 bg-white"
                      data-testid="b29-header-branch"
                    />
                  </div>
                  <div>
                    <Label className="text-[9px] font-bold uppercase tracking-wider text-gray-500">Person</Label>
                    <Input
                      value={builderPerson}
                      onChange={(e) => setBuilderPerson(e.target.value)}
                      className="h-9 w-36 text-sm rounded-xl border-2 border-gray-300 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 bg-white"
                      data-testid="b29-header-person"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 flex-nowrap justify-end flex-shrink-0 ml-auto">
                {view === 'list' && (
                  <button
                    onClick={() => setView('choose')}
                    className={`${headerBtnClass} border-orange-400 text-orange-700 hover:bg-orange-50`}
                    data-testid="b29-new-transfer-btn"
                  >
                    <Plus className="w-3.5 h-3.5" /> New Transfer
                  </button>
                )}
                {view === 'orders' && (
                  <button
                    disabled={pickedLines.length === 0}
                    onClick={() => onOrdersPicked(pickedLines)}
                    className={`${headerBtnClass} text-white border-orange-500`}
                    style={{ backgroundColor: ORANGE }}
                    data-testid="b29-recent-orders-continue-btn"
                  >
                    Continue with {pickedLines.length} item(s)
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-md transition-colors flex-shrink-0"
                  title="Close"
                  data-testid="b29-modal-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="v3-modal-body p-4 space-y-3 overflow-y-auto flex-1 min-h-0 [overscroll-behavior:contain]">
              {view === 'list' && (
                <>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                      <MonthPill range={range} onChange={setRange} />
                      <span className="text-xs text-gray-500" data-testid="b29-range-note">
                        {isThisMonth ? 'Showing this month\'s transfers only' : 'Showing transfers for the selected period only'}
                        {' '}({fmtDMY(range.from)} – {fmtDMY(range.to)}) · {loading ? '…' : `${transfers.length} transfer${transfers.length === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  </div>

                  {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} /></div>
                  ) : transfers.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      <ClipboardList className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                      <p className="text-sm">No B29 transfers dated in this period.</p>
                      <p className="text-xs mt-1">Use the month pill above to look at another month or a date range.</p>
                    </div>
                  ) : (
                    <div className={`grid gap-3 ${panelOpen ? 'grid-cols-1' : 'grid-cols-2'}`} data-testid="b29-transfers-list">
                      {transfers.map((t) => (
                        <TransferCard
                          key={t.id}
                          transfer={t}
                          onChanged={load}
                          onEdit={openBuilder}
                          onEmail={setEmailTransfer}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {view === 'choose' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="b29-new-transfer-choice">
                  <button
                    onClick={() => setView('orders')}
                    className="rounded-2xl border-2 border-orange-300 bg-white p-6 text-left hover:bg-orange-50 hover:shadow-md transition-all"
                    data-testid="b29-choice-recent-orders"
                  >
                    <p className="font-extrabold text-gray-900">Build from Recent Orders</p>
                    <p className="text-xs text-gray-500 mt-1">Pick lines from a recent sales order — the order number and customer come across with them.</p>
                  </button>
                  <button
                    onClick={() => openBuilder(null)}
                    className="rounded-2xl border-2 border-orange-300 bg-white p-6 text-left hover:bg-orange-50 hover:shadow-md transition-all"
                    data-testid="b29-choice-from-scratch"
                  >
                    <p className="font-extrabold text-gray-900">Build from Scratch</p>
                    <p className="text-xs text-gray-500 mt-1">Start with a blank transfer and add the lines yourself.</p>
                  </button>
                </div>
              )}

              {view === 'orders' && <RecentOrdersPicker selectedItems={pickedLines} setSelectedItems={setPickedLines} />}

              {view === 'builder' && (
                <TransferBuilder
                  key={builderInitial?.id || `new-${builderKey}`}
                  initial={builderInitial}
                  branch={builderBranch}
                  person={builderPerson}
                  onSaved={onSaved}
                  onCancel={goBack}
                  onRefresh={load}
                  onEmail={setEmailTransfer}
                  onDraft={setDraft}
                />
              )}
            </div>
          </div>

          {/* ── Slide-out panel (View Price Lists) — grows out to the RIGHT of the B29 modal ── */}
          <div
            className="hidden md:block flex-shrink-0 h-full rounded-3xl border-2 border-orange-300 bg-white shadow-2xl overflow-hidden"
            style={{
              width: panelOpen ? HALF_WIDTH : 0,
              minWidth: panelOpen ? 320 : 0,
              marginLeft: panelOpen ? SHELL_GAP : 0,
              opacity: panelOpen ? 1 : 0,
              borderWidth: panelOpen ? 2 : 0,
              transition: 'width 300ms ease, margin 300ms ease, opacity 200ms ease',
            }}
            data-testid="b29-side-panel"
            aria-hidden={!panelOpen}
          >
            {panelOpen && <PriceListPanel />}
          </div>
        </div>
      </div>

      {emailTransfer && (
        <EmailComposeModal
          transfer={emailTransfer}
          user={user}
          onClose={() => setEmailTransfer(null)}
          onSent={load}
        />
      )}
    </>
  );
};

/** Standalone page wrapper for the /b29-stock-transfer route (kept working
 * for deep links/bookmarks) — renders the same Modal, always open. */
const B29StockTransfer = ({ user }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-100">
      <AppHeader
        user={user}
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <Package className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">B29 Stock Transfer</span>
          </div>
        }
      />
      <B29Modal isOpen={true} onClose={() => navigate(window.history.length > 1 ? -1 : '/stock-transfer')} user={user} />
    </div>
  );
};

B29StockTransfer.Modal = B29Modal;

export default B29StockTransfer;
